import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import './CreateAccountModal.css';

// Free-account signup that stays on the page it was opened from, so a search a
// user is mid-way through building survives creating an account to save it.
function CreateAccountModal({ open, onClose, onCreated, onLogin, reason }) {
  const { registerFree } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [optIn, setOptIn] = useState(true);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const emailRef = useRef(null);
  const dialogRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    setError('');
    setSubmitting(false);
    setTermsAccepted(false);
    emailRef.current?.focus();
    const onKeyDown = event => { if (event.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);

  if (!open) return null;

  const handleSubmit = async event => {
    event.preventDefault();
    setError('');
    if (!termsAccepted) {
      setError('You must agree to the Terms to create an account.');
      return;
    }
    if (password.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }
    setSubmitting(true);
    try {
      const user = await registerFree(email, password, optIn);
      onCreated?.(user);
    } catch (err) {
      setError(err.message || 'Could not create your account.');
      setSubmitting(false);
    }
  };

  return (
    <div className="ca-backdrop" onMouseDown={event => {
      if (!dialogRef.current?.contains(event.target)) onClose();
    }}>
      <div className="ca-dialog" role="dialog" aria-modal="true" aria-labelledby="ca-title" ref={dialogRef}>
        <button className="ca-close" type="button" onClick={onClose} aria-label="Close">×</button>
        <h2 id="ca-title">Create your free COIN account</h2>
        <p className="ca-lede">{reason || 'Save your searches and re-run them any time.'}</p>
        {error && <p className="ca-error" role="alert">{error}</p>}
        <form onSubmit={handleSubmit}>
          <label className="ca-field">
            <span>Email</span>
            <input ref={emailRef} type="email" value={email} required autoComplete="email"
              onChange={event => setEmail(event.target.value)} disabled={submitting} />
          </label>
          <label className="ca-field">
            <span>Password</span>
            <input type="password" value={password} required autoComplete="new-password"
              placeholder="At least 6 characters" minLength={6}
              onChange={event => setPassword(event.target.value)} disabled={submitting} />
          </label>
          <label className="ca-check">
            <input type="checkbox" checked={optIn} disabled={submitting}
              onChange={event => setOptIn(event.target.checked)} />
            <span>
              Send me card market insights, strategies, new features, and opportunities by email.
              <small className="ca-check-note">Unsubscribe anytime.</small>
            </span>
          </label>
          <label className="ca-check">
            <input type="checkbox" checked={termsAccepted} required disabled={submitting}
              onChange={event => setTermsAccepted(event.target.checked)} />
            <span>I agree to the <Link to="/terms">Terms</Link>.</span>
          </label>
          <button className="ca-submit" type="submit" disabled={submitting || !termsAccepted}>
            {submitting ? 'Creating account…' : 'Create Account'}
          </button>
        </form>
        <p className="ca-footnote">
          Already have an account? <Link to="/login?redirect=/search" onClick={onLogin}>Log in</Link>
        </p>
      </div>
    </div>
  );
}

export default CreateAccountModal;
